'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage, ChatState } from './ChatLayout';

interface TextChatPanelProps {
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
  chatState: ChatState;
}

function formatTime(date: Date): string {
  const h = date.getHours();
  const m = date.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

function MessageTime({ timestamp }: { timestamp: Date }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return <span className="text-xs text-muted-foreground mt-1 inline-block">&nbsp;</span>;
  return <span className="text-xs text-muted-foreground mt-1 inline-block">{formatTime(timestamp)}</span>;
}

export default function TextChatPanel({
  messages,
  onSendMessage,
  chatState,
}: TextChatPanelProps) {
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (chatState !== 'chatting') return;
    const show = setTimeout(() => setIsTyping(true), 3000);
    const hide = setTimeout(() => setIsTyping(false), 5500);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, [chatState, messages.length]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || chatState !== 'chatting') return;
    onSendMessage(text);
    setInputText('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = chatState === 'chatting';

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-shrink-0">
        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <span className="text-sm font-semibold text-foreground">Live Chat</span>
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground tabular-nums">{messages.length} messages</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 && canSend && (
          <div className="flex flex-col items-center justify-center h-full gap-2 opacity-50">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-xs text-muted-foreground text-center">Say hello! Be kind and respectful.</p>
          </div>
        )}

        {messages.length === 0 && !canSend && (
          <div className="flex flex-col items-center justify-center h-full gap-2 opacity-40">
            <p className="text-xs text-muted-foreground text-center">
              {chatState === 'searching' ? 'Finding a match...' : 'Start chatting to see messages here'}
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === 'self' ? 'justify-end' : 'justify-start'} slide-up`}
          >
            <div className="max-w-[85%]">
              <div className={`px-3 py-2 text-sm leading-relaxed ${
                msg.sender === 'self' ? 'chat-bubble-self' : 'chat-bubble-other'
              }`}>
                {msg.text}
              </div>
              <p className={`mt-1 ${msg.sender === 'self' ? 'text-right' : 'text-left'}`}>
                <MessageTime timestamp={msg.timestamp} />
              </p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start slide-up">
            <div className="chat-bubble-other px-3 py-2.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground pulse-dot" style={{ animationDelay: '0s' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground pulse-dot" style={{ animationDelay: '0.2s' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground pulse-dot" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t border-border flex-shrink-0">
        <div className={`flex items-end gap-2 rounded-xl border transition-all ${
          canSend
            ? 'border-border bg-card focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
            : 'border-border bg-muted opacity-60'
        }`}>
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!canSend}
            placeholder={canSend ? 'Type a message... (Enter to send)' : 'Connect to start chatting'}
            rows={1}
            className="flex-1 bg-transparent px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none max-h-24 overflow-y-auto"
            style={{ minHeight: '40px' }}
          />
          <button
            onClick={handleSend}
            disabled={!canSend || !inputText.trim()}
            className={`m-1.5 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 flex-shrink-0 ${
              canSend && inputText.trim()
                ? 'bg-primary text-white hover:bg-primary/90 active:scale-95'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
            aria-label="Send message"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 text-center">
          Be respectful · No personal info · Report abuse
        </p>
      </div>
    </div>
  );
}
